import { Alert, AlertTitle, Box, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { AcademySkillPart } from '../../../pages/academy/Academy/AcademyService';
import lockOutlinedIconUrl from './Images/lock-outlined.svg';
import refreshRoundedIconUrl from './Images/refresh-rounded.svg';
import thumbUpAltOutlinedIconUrl from './Images/thumb-up-alt-outlined.svg';
import warningAmberRoundedIconUrl from './Images/warning-amber-rounded.svg';
import {
  getAcademyMaskedIconProps,
} from '../Utilities/academyMaskedIcon';
import { skillCheckNoticeClassNames, skillCheckNoticeStyles } from './SkillCheckNotice.styles';

export type SkillCheckNoticeStatus = 'teacher_allowed' | 'teacher_blocked' | 'error';

export interface SkillCheckNoticeState {
  part: AcademySkillPart;
  status: SkillCheckNoticeStatus;
  messages: string[];
}

export interface SkillCheckNoticeProps {
  notice: SkillCheckNoticeState | null;
  part: AcademySkillPart;
  isChecking: boolean;
  showRefresh?: boolean;
  showConfirmAnyway?: boolean;
  onRefresh: () => void;
  onConfirmAnyway: () => void;
}

export function SkillCheckNotice({
  notice,
  part,
  isChecking,
  onRefresh,
  onConfirmAnyway,
  showRefresh = true,
  showConfirmAnyway = true,
}: SkillCheckNoticeProps) {
  const theme = useTheme();

  if (!notice) {
    return null;
  }

  if (notice.status === 'error') {
    return (
      <Alert
        className={skillCheckNoticeClassNames.message}
        sx={skillCheckNoticeStyles.message()}
        role="alert"
        severity="error"
      >
        {notice.messages.join(' ')}
      </Alert>
    );
  }

  const teacherCanConfirm = notice.status === 'teacher_allowed';
  const title = getSkillCheckNoticeTitle(teacherCanConfirm);
  const actionLabel = part === 'apply' ? 'Mark as Applied Anyway' : 'Mark as Learned Anyway';
  const noticeClassName = `${skillCheckNoticeClassNames.notice} ${
    teacherCanConfirm ? skillCheckNoticeClassNames.canConfirm : skillCheckNoticeClassNames.blocked
  }`;

  return (
    <Box className={skillCheckNoticeClassNames.action} sx={skillCheckNoticeStyles.action()}>
      <Alert
        className={noticeClassName}
        sx={skillCheckNoticeStyles.notice(theme, teacherCanConfirm)}
        role="alert"
        severity={teacherCanConfirm ? 'warning' : 'error'}
        icon={teacherCanConfirm ? <WarningIcon /> : <LockIcon />}
        action={
          showRefresh ? (
            <Button
              type="button"
              className={skillCheckNoticeClassNames.refreshButton}
              sx={skillCheckNoticeStyles.refreshButton(theme)}
              disableRipple
              disabled={isChecking}
              onClick={onRefresh}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          ) : null
        }
      >
        <Box className={skillCheckNoticeClassNames.copy} sx={skillCheckNoticeStyles.copy()}>
          <AlertTitle
            className={skillCheckNoticeClassNames.title}
            sx={skillCheckNoticeStyles.title()}
          >
            {title}
          </AlertTitle>
          <Box component="ol" sx={skillCheckNoticeStyles.list()}>
            {notice.messages.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </Box>
        </Box>
      </Alert>
      {teacherCanConfirm && showConfirmAnyway ? (
        <Button
          type="button"
          className={`${skillCheckNoticeClassNames.applyButton} ${skillCheckNoticeClassNames.anywayButton}`}
          sx={skillCheckNoticeStyles.applyAnywayButton()}
          disableRipple
          disabled={isChecking}
          onClick={onConfirmAnyway}
          startIcon={<ThumbsUpIcon />}
        >
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
}

function getSkillCheckNoticeTitle(teacherCanConfirm: boolean): string {
  if (teacherCanConfirm) {
    return 'We show the following incomplete actions. We recommend you complete them.';
  }

  return 'Please complete the following required actions. Refresh to update information.';
}

function ThumbsUpIcon() {
  return <Box component="span" {...getAcademyMaskedIconProps(thumbUpAltOutlinedIconUrl)} />;
}

function WarningIcon() {
  return <Box component="span" {...getAcademyMaskedIconProps(warningAmberRoundedIconUrl)} />;
}

function LockIcon() {
  return <Box component="span" {...getAcademyMaskedIconProps(lockOutlinedIconUrl)} />;
}

function RefreshIcon() {
  return <Box component="span" {...getAcademyMaskedIconProps(refreshRoundedIconUrl)} />;
}
